import { useMemo } from 'react';

import type { ContentMenuNode, HubMenuSection } from '../utils/contentTree';

import { useInfoHubMenu } from './useInfoHubMenu';
import { findSectionKey } from '../utils/hubSections';

/** One block in the rail: a heading, and the pages filed under it. */
export interface RailBlock {
  key: string;
  /** The block's heading. */
  title: string;
  /** Where the heading links, or empty when nothing backs it. */
  to: string;
  children: ContentMenuNode[];
}

export interface SectionRailState {
  /** The section the current page sits in, or null when no section claims it. */
  section: HubMenuSection | null;
  /** What the rail is currently rooted at — the section itself, or a page inside it. */
  rootTitle: string;
  /**
   * `slugFull` of the page one level up — where Back goes. Empty when no section
   * claims the page and there is nothing above it to name.
   */
  parentTo: string;
  blocks: RailBlock[];
  loading: boolean;
}

const segments = (slugFull: string): string[] => (slugFull || '').split('/').filter(Boolean);

/** Every node in a section's tree, by its full path. */
const indexBySlug = (
  nodes: ContentMenuNode[],
  into: Map<string, ContentMenuNode> = new Map(),
): Map<string, ContentMenuNode> => {
  nodes.forEach((node) => {
    into.set(node.slugFull, node);
    indexBySlug(node.children, into);
  });
  return into;
};

/**
 * The reference rail's contents for a page.
 *
 * THE RAIL RE-ROOTS AS YOU DRILL IN. Open Annual National Competitions and the
 * rail becomes *its* contents — one block per child that has children, the rest
 * gathered under its own name. It is not pinned to the section, because a rail
 * showing the whole of Sport Shooting is answering a question you stopped asking
 * two clicks ago.
 *
 * The root is the deepest ancestor-or-self of the current page that actually has
 * children, walking up until it finds one, and falling back to the section. That
 * fallback is what keeps a leaf page from emptying the rail: land on a page with
 * nothing beneath it and you get its parent's contents — your own siblings, with
 * you marked among them — rather than a blank column.
 *
 * BLOCKS ARE THE CHILDREN THAT HAVE CHILDREN OF THEIR OWN. A childless child
 * would be an empty bounded block, which is a heading promising a list and then
 * not having one; those gather into a single block headed with the root's name
 * instead, since that is what they are — pages filed directly under it. It
 * leads, because a page sitting loose is above the sub-groups, not after them.
 */
export const useSectionRail = (slugFull: string): SectionRailState => {
  const { sections, loading } = useInfoHubMenu();

  const section = useMemo(() => {
    const key = findSectionKey(slugFull);
    return key ? (sections.find((candidate) => candidate.key === key) ?? null) : null;
  }, [sections, slugFull]);

  const root = useMemo(() => {
    if (!section) {
      return null;
    }
    const byPath = indexBySlug(section.children);
    const parts = segments(slugFull);
    // Deepest first: the page itself, then each ancestor, then the section.
    for (let depth = parts.length; depth > 0; depth -= 1) {
      const node = byPath.get(parts.slice(0, depth).join('/'));
      if (node && node.children.length > 0) {
        return node;
      }
    }
    return null;
  }, [section, slugFull]);

  const blocks = useMemo<RailBlock[]>(() => {
    if (!section) {
      return [];
    }

    const children = root ? root.children : section.children;
    const title = root ? root.title : section.label;
    const to = root ? (root.navigable ? root.slugFull : '') : section.to;

    const grouped = children.filter((child) => child.children.length > 0);
    const loose = children.filter((child) => child.children.length === 0);

    return [
      ...(loose.length
        ? [{ key: `root:${root?.id ?? section.key}`, title, to, children: loose }]
        : []),
      ...grouped.map((child) => ({
        key: child.id,
        title: child.title,
        // A synthesised level has no page to open, so its heading is inert.
        to: child.navigable ? child.slugFull : '',
        children: child.children,
      })),
    ];
  }, [section, root]);

  /**
   * The page one level up, for the Back control.
   *
   * The NEAREST NAVIGABLE ancestor, not simply the slug with its last segment
   * removed. The import is full of levels the path implies but no visible page
   * occupies — 20 of them — and linking one would land the reader on a 404, so
   * this keeps walking up until it finds a page that actually opens. A first-level
   * child has no ancestor inside the section tree (the section's own node is
   * lifted out when the menu is built), so it falls through to the section
   * landing, which `buildHubMenu` has already resolved to a real page.
   */
  const parentTo = useMemo(() => {
    if (!section) {
      return '';
    }
    const byPath = indexBySlug(section.children);
    const parts = segments(slugFull);
    for (let depth = parts.length - 1; depth > 0; depth -= 1) {
      const node = byPath.get(parts.slice(0, depth).join('/'));
      if (node?.navigable) {
        return node.slugFull;
      }
    }
    return section.to;
  }, [section, slugFull]);

  return {
    section,
    rootTitle: root ? root.title : (section?.label ?? ''),
    parentTo,
    blocks,
    loading,
  };
};
